import { toMilliseconds } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { isScrolledToEnd, scrollToEnd } from '@/lib/scrollLib';
import { ScanDataMutators, useAuth, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import {
  getFullStatusDetailsHistoryFromScan,
  getFullStatusDetailsHistoryWithLastEntryEditable,
  getScanPartUpdatesForProgressUpdate,
  ProgressUpdate,
  ScanPartOptimized,
} from '@kaiizen/kai-scan-template-system';
import { LAYOUT2 } from '../../../Styles';
import { TaskProgressCard } from './TaskProgressCard';

const PROGRESS_HISTORY_SCROLL_CONTAINER_STYLE = css`
  label: PROGRESS_HISTORY_SCROLL_CONTAINER_STYLE;
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 0 ${LAYOUT2.spacing1}px ${LAYOUT2.spacing1}px ${LAYOUT2.spacing1}px;
  overflow-x: auto;
  overflow-y: hidden;
`;

const PROGRESS_HISTORY_ALIGNER_STYLE = css`
  label: PROGRESS_HISTORY_ALIGNER_STYLE;
  display: flex;
  justify-content: flex-end;
  width: max-content;
  min-width: 100%;
  height: 100%;
`;

const PROGRESS_HISTORY_ROW_STYLE = css`
  label: PROGRESS_HISTORY_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  height: 100%;
`;

export const TaskProgressEditor = ({ scanId, scanPart, scanDataMutators, groupId, taskId, optionId, lineItemId }: {
  scanId: string;
  scanPart: ScanPartOptimized;
  scanDataMutators: ScanDataMutators;
  groupId?: string;
  taskId?: string;
  optionId?: string;
  lineItemId?: string;
}) => {
  const { user } = useAuth();
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const shouldStickToEndRef = useRef(true);

  // Get the full history with the editable entry guaranteed at the end
  // No useMemo - this is a lightweight computation and ensures fresh data on every render
  const progressHistory = getFullStatusDetailsHistoryWithLastEntryEditable(
    getFullStatusDetailsHistoryFromScan(scanPart),
  );

  // Stick-to-right behavior (chat-style) without first-paint jiggle.
  useLayoutEffect(() => {
    if (shouldStickToEndRef.current) scrollToEnd(scrollContainerRef.current);
  }, []);

  useLayoutEffect(() => {
    if (shouldStickToEndRef.current) scrollToEnd(scrollContainerRef.current);
  }, [progressHistory.length]);

  useEffect(() => {
    const scrollContainerElement = scrollContainerRef.current;
    const rowElement = rowRef.current;
    if (!scrollContainerElement || !rowElement) return;

    const resizeObserver = new ResizeObserver(() => {
      if (shouldStickToEndRef.current) scrollToEnd(scrollContainerRef.current);
    });

    resizeObserver.observe(scrollContainerElement);
    resizeObserver.observe(rowElement);

    return () => resizeObserver.disconnect();
  }, []);

  const onScroll = useCallback(() => {
    shouldStickToEndRef.current = isScrolledToEnd(scrollContainerRef.current);
  }, []);

  const handleStatusChange = useCallback((update: Partial<ProgressUpdate>) => {
    scanDataMutators.updateScanPart(
      { groupId, taskId, optionId, lineItemId },
      getScanPartUpdatesForProgressUpdate(scanPart, {
        ...update,
        updatedAt: new Date(),
        updatedByUserId: user?.id,
      }),
    );
  }, [groupId, taskId, optionId, lineItemId, user?.id, scanDataMutators, scanPart]);

  return (
    <div ref={scrollContainerRef} css={PROGRESS_HISTORY_SCROLL_CONTAINER_STYLE} onScroll={onScroll}>
      <div css={PROGRESS_HISTORY_ALIGNER_STYLE}>
        <div ref={rowRef} css={PROGRESS_HISTORY_ROW_STYLE}>
          {progressHistory.map((fullStatusDetails, index) => {
            const isLastEntry = index === progressHistory.length - 1;
            const isEditable = isLastEntry && scanIsEditable;

            return (
              <TaskProgressCard
                key={toMilliseconds(fullStatusDetails.updatedAt)}
                scanPart={scanPart}
                scanId={scanId}
                groupId={groupId}
                taskId={taskId}
                fullStatusDetails={fullStatusDetails}
                editable={isEditable}
                onStatusChange={isEditable ? handleStatusChange : undefined}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
